import { registerLocaleData } from '@angular/common';
import localeAr from '@angular/common/locales/ar';

import { normalizeStoredLanguage, readStoredLanguage, readUrlLanguageOverride } from './language-storage';

/**
 * Angular locale data behind the built-in `date`/`number` pipes.
 *
 * Without this the pipes always format with Angular's default `en-US` data,
 * so the Arabic UI showed English month names ("May 5, 2026"). CLDR's Arabic
 * data already keeps Western digits and `.`/`,` separators (product rule:
 * numbers stay in English digits), but its *medium* date is numeric
 * (`dd/MM/y`); switch it to the month-name form so `mediumDate` / `medium`
 * read "5 مايو 2026" like the Intl-based formatters elsewhere in the app.
 */
const ARABIC_LOCALE_ID = 'ar';
const DATE_FORMATS_INDEX = 10; // [short, medium, long, full]
const MEDIUM = 1;

function arabicLocaleWithMonthNames(): unknown[] {
  const data = [...(localeAr as unknown[])];
  const dateFormats = [...(data[DATE_FORMATS_INDEX] as string[])];
  dateFormats[MEDIUM] = 'd MMM y';
  data[DATE_FORMATS_INDEX] = dateFormats;
  return data;
}

let registered = false;

export function registerAppLocales(): void {
  if (registered) return;
  registerLocaleData(arabicLocaleWithMonthNames(), ARABIC_LOCALE_ID);
  registered = true;
}

/** Angular LOCALE_ID for a UI language. Only Arabic is localized; others keep en-US formatting. */
export function angularLocaleId(lang: string): string {
  return lang.toLowerCase().startsWith('ar') ? ARABIC_LOCALE_ID : 'en-US';
}

/**
 * LOCALE_ID is read once when the app bootstraps (before translations load), so
 * resolve it from the same persisted signals the translation service uses.
 * Switching language through the topbar reloads the page, which re-resolves it.
 */
export function initialAngularLocaleId(): string {
  registerAppLocales();
  const lang = readUrlLanguageOverride() ?? readStoredLanguage() ?? 'en-US';
  return angularLocaleId(normalizeStoredLanguage(lang));
}
